/**
 * Share / export: every chart can leave the site as an image sized for social, with a caption and
 * a link back to the exact game. The image is drawn on a canvas from the same geometry as the
 * on-screen chart (chart.ts) and the same theme tokens as the page, so a post looks like the site.
 *
 * Facts only, like the rest of BestOpen: the caption says what moved and by how much, never what
 * to bet.
 */
import { fmtTick, plotScale, stepPath } from "./chart";
import type { SeriesPoint } from "./history";

export type CardSize = "x" | "ig";

/** X / link-preview landscape, and Instagram portrait. */
export const CARD_SIZES: Record<CardSize, { w: number; h: number; label: string }> = {
  x: { w: 1200, h: 675, label: "X" },
  ig: { w: 1080, h: 1350, label: "Instagram" },
};

export interface CardSeries {
  label: string;
  points: SeriesPoint[];
  /** Which theme colour draws it. */
  tone: "accent" | "accent-2";
}

export interface ChartCard {
  /** "NFL · Sun, Sep 28 · 7:15 PM" */
  eyebrow: string;
  /** "Eagles @ Bears" */
  title: string;
  /** "Moneyline" */
  market: string;
  /** The one fact the card is about: "Eagles moved from −150 to −188". */
  headline: string;
  /** "Last 24 hours · best price across venues" */
  sub: string;
  series: CardSeries[];
  /** Axis label for a price, in the viewer's odds format. */
  yLabel: (price: number) => string;
  /** Printed in the footer: where the card lives. */
  link: string;
  /** News pins drawn on the chart (the card shows one, marked the same as its headline). */
  pins?: { t: number }[];
  /** The pinned headline, shown under the sub line: "Sep 26 · Bears QB out (ESPN)". */
  note?: string;
}

function token(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--color-${name}`).trim();
  return v || fallback;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/** Truncate with an ellipsis to fit `max` pixels. */
function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > max) s = s.slice(0, -1);
  return `${s}…`;
}

/** Break `text` into at most `lines` lines that fit `max` pixels; the last one is truncated. */
function wrap(ctx: CanvasRenderingContext2D, text: string, max: number, lines: number): string[] {
  const out: string[] = [];
  let cur = "";
  const words = text.split(" ");
  for (let i = 0; i < words.length; i++) {
    const next = cur ? `${cur} ${words[i]}` : words[i];
    if (ctx.measureText(next).width <= max || !cur) {
      cur = next;
      continue;
    }
    if (out.length === lines - 1) {
      cur = `${cur} ${words.slice(i).join(" ")}`;
      break;
    }
    out.push(cur);
    cur = words[i];
  }
  out.push(fit(ctx, cur, max));
  return out;
}

/** Draw a share card and return it as a PNG. */
export async function renderChartCard(card: ChartCard, size: CardSize): Promise<Blob> {
  const { w, h } = CARD_SIZES[size];
  const tall = size === "ig";
  const c = {
    bg: token("bg", "#09090f"),
    card: token("card", "#15152a"),
    line: token("line", "#25253f"),
    ink: token("ink", "#f5f4ff"),
    dim: token("dim", "#a3a1c7"),
    faint: token("faint", "#6b6993"),
    accent: token("accent", "#a78bfa"),
    accent2: token("accent-2", "#f472b6"),
  };
  const display = '"Bricolage Grotesque", Inter, system-ui, sans-serif';
  const sans = "Inter, system-ui, sans-serif";
  await Promise.all(
    [`800 40px ${display}`, `700 20px ${sans}`, `600 20px ${sans}`, `500 20px ${sans}`].map((f) =>
      document.fonts.load(f).catch(() => null),
    ),
  );
  const icon = await loadImage("/icon.svg");

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas isn't available");

  // Ground + the site's top glow.
  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, w, h);
  const glow = ctx.createRadialGradient(w / 2, -h * 0.35, 0, w / 2, -h * 0.35, w * 0.75);
  // Alpha via globalAlpha, not a hex suffix, so any CSS colour format the theme uses works.
  glow.addColorStop(0, c.accent);
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 1;

  const brand = ctx.createLinearGradient(0, 0, w, h);
  brand.addColorStop(0, c.accent);
  brand.addColorStop(1, c.accent2);

  const m = tall ? 72 : 64;
  let y = m;

  // Wordmark.
  const iconSize = 44;
  if (icon) ctx.drawImage(icon, m, y, iconSize, iconSize);
  ctx.font = `800 32px ${display}`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = c.ink;
  const bx = m + (icon ? iconSize + 14 : 0);
  ctx.fillText("Best", bx, y + iconSize / 2 + 1);
  ctx.fillStyle = brand;
  ctx.fillText("Open", bx + ctx.measureText("Best").width, y + iconSize / 2 + 1);

  // Market pill, top right.
  ctx.font = `700 20px ${sans}`;
  const pill = card.market.toUpperCase();
  const pw = ctx.measureText(pill).width + 36;
  roundRect(ctx, w - m - pw, y + 2, pw, 40, 20);
  ctx.fillStyle = c.card;
  ctx.fill();
  ctx.strokeStyle = c.line;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = c.dim;
  ctx.fillText(pill, w - m - pw + 18, y + 23);

  // Eyebrow, title, headline.
  ctx.textBaseline = "alphabetic";
  y += iconSize + (tall ? 72 : 48);
  ctx.font = `600 ${tall ? 24 : 20}px ${sans}`;
  ctx.fillStyle = c.faint;
  // Wide card with a headline note: the sub line joins the eyebrow so the chart keeps its height.
  const foldSub = !tall && !!card.note;
  const eyebrow = foldSub ? `${card.eyebrow} · ${card.sub.split(" · ")[0]}` : card.eyebrow;
  ctx.fillText(fit(ctx, eyebrow.toUpperCase(), w - 2 * m), m, y);
  y += tall ? 64 : 52;
  ctx.font = `800 ${tall ? 60 : 48}px ${display}`;
  ctx.fillStyle = c.ink;
  const titleLines = wrap(ctx, card.title, w - 2 * m, tall ? 2 : 1);
  titleLines.forEach((line, i) => ctx.fillText(line, m, y + i * (tall ? 68 : 56)));
  y += (titleLines.length - 1) * (tall ? 68 : 56);
  y += tall ? 60 : 48;
  ctx.font = `700 ${tall ? 36 : 30}px ${sans}`;
  ctx.fillStyle = brand;
  ctx.fillText(fit(ctx, card.headline, w - 2 * m), m, y);
  if (!foldSub) {
    y += tall ? 44 : 36;
    ctx.font = `500 ${tall ? 24 : 20}px ${sans}`;
    ctx.fillStyle = c.dim;
    ctx.fillText(fit(ctx, card.sub, w - 2 * m), m, y);
  }

  // The pinned headline, with its number badge.
  if (card.note) {
    y += tall ? 52 : 40;
    let nx = m;
    if (card.pins?.length) {
      badge(ctx, m + 12, y - 7, 12, c);
      nx = m + 34;
    }
    ctx.font = `600 ${tall ? 24 : 20}px ${sans}`;
    ctx.fillStyle = c.ink;
    ctx.textBaseline = "alphabetic";
    ctx.fillText(fit(ctx, card.note, w - m - nx), nx, y);
  }

  // Chart panel.
  const footer = tall ? 120 : 84;
  const panel = { x: m, y: y + (tall ? 48 : 28), w: w - 2 * m, h: 0 };
  panel.h = h - footer - panel.y;
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 24);
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = c.card;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = c.line;
  ctx.stroke();

  const legendH = 44;
  const pinRoom = card.pins?.length ? 36 : 0;
  const box = {
    x: panel.x + 96,
    y: panel.y + legendH + 20 + pinRoom,
    w: panel.w - 96 - 32,
    h: panel.h - legendH - 20 - 56 - pinRoom,
  };
  const plot = plotScale(
    card.series.map((s) => ({ sideKey: s.label, label: s.label, points: s.points })),
    box,
  );

  // Legend.
  ctx.font = `700 20px ${sans}`;
  let lx = panel.x + 28;
  for (const s of card.series) {
    ctx.fillStyle = s.tone === "accent" ? c.accent : c.accent2;
    roundRect(ctx, lx, panel.y + 30, 24, 6, 3);
    ctx.fill();
    ctx.fillStyle = c.ink;
    ctx.fillText(s.label, lx + 34, panel.y + 40);
    lx += 34 + ctx.measureText(s.label).width + 36;
  }

  if (plot) {
    ctx.font = `500 18px ${sans}`;
    ctx.lineWidth = 1;
    // Tight chart: label every other gridline so the labels don't collide.
    const every = box.h / Math.max(1, plot.yTicks.length - 1) < 34 ? 2 : 1;
    for (const [i, v] of plot.yTicks.entries()) {
      const gy = plot.y(v);
      ctx.strokeStyle = c.line;
      ctx.beginPath();
      ctx.moveTo(box.x, gy);
      ctx.lineTo(box.x + box.w, gy);
      ctx.stroke();
      if (i % every !== 0) continue;
      ctx.fillStyle = c.faint;
      ctx.textAlign = "right";
      ctx.fillText(card.yLabel(v), box.x - 16, gy + 6);
    }
    const span = plot.t1 - plot.t0;
    plot.xTicks.forEach((t, i) => {
      ctx.textAlign = i === 0 ? "left" : i === plot.xTicks.length - 1 ? "right" : "center";
      ctx.fillStyle = c.faint;
      ctx.fillText(fmtTick(t, span), plot.x(t), box.y + box.h + 36);
    });
    ctx.textAlign = "left";

    for (const pin of card.pins ?? []) {
      const px = plot.x(pin.t);
      ctx.save();
      ctx.strokeStyle = c.dim;
      ctx.globalAlpha = 0.5;
      ctx.setLineDash([3, 6]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, box.y - 6);
      ctx.lineTo(px, box.y + box.h);
      ctx.stroke();
      ctx.restore();
      badge(ctx, px, box.y - 20, 12, c);
    }

    for (const s of card.series) {
      if (!s.points.length) continue;
      const color = s.tone === "accent" ? c.accent : c.accent2;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      ctx.lineJoin = "round";
      ctx.shadowColor = color;
      ctx.shadowBlur = 14;
      ctx.stroke(new Path2D(stepPath(s.points, plot)));
      ctx.restore();
      const last = s.points[s.points.length - 1];
      ctx.beginPath();
      ctx.arc(plot.x(last.t), plot.y(last.price), 7, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    }
  }

  // Footer.
  ctx.font = `600 ${tall ? 24 : 20}px ${sans}`;
  ctx.fillStyle = c.dim;
  const tagline = "Facts, not picks · Built on OpenMarkets";
  if (tall) {
    // Stacked: the link, then the tagline.
    ctx.fillText(fit(ctx, card.link, w - 2 * m), m, h - footer + 52);
    ctx.fillStyle = c.faint;
    ctx.fillText(tagline, m, h - footer + 88);
  } else {
    const fy = h - footer / 2 + 8;
    ctx.fillText(fit(ctx, card.link, w * 0.55), m, fy);
    ctx.textAlign = "right";
    ctx.fillStyle = c.faint;
    ctx.fillText(tagline, w - m, fy);
    ctx.textAlign = "left";
  }

  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't make the image"))), "image/png"),
  );
}

/** The news marker: a ring with a dot, on the chart and beside the headline it points to. */
function badge(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: { card: string; dim: string; ink: string }) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = c.card;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = c.dim;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, r * 0.38, 0, Math.PI * 2);
  ctx.fillStyle = c.ink;
  ctx.fill();
  ctx.restore();
}

export type ShareOutcome = "shared" | "downloaded" | "cancelled";

/**
 * Hand the image off: the phone's share sheet where it takes files (straight into X, Instagram,
 * Messages), otherwise download the PNG and put the caption + link on the clipboard.
 */
export async function shareImage(
  blob: Blob,
  opts: { filename: string; caption: string; url: string; preferDownload?: boolean },
): Promise<ShareOutcome> {
  const file = new File([blob], opts.filename, { type: "image/png" });
  const text = `${opts.caption}\n${opts.url}`;
  if (!opts.preferDownload && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      return "shared";
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return "cancelled";
      // Fall through to a download.
    }
  }
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = opts.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  await navigator.clipboard?.writeText(text).catch(() => null);
  return "downloaded";
}

/** A game's own page — what every shared card links back to. */
export function gameUrl(contestId: string): string {
  return `${window.location.origin}/game/${encodeURIComponent(contestId)}`;
}

/** "eagles-at-bears-moneyline.png" */
export function cardFilename(...parts: string[]): string {
  const slug = parts
    .join(" ")
    .toLowerCase()
    .replace(/@/g, " at ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `bestopen-${slug || "card"}.png`;
}
